import type { TermLabel } from "../../../api/content/ontologyApi";
import { isJsonObject, type JsonObject } from "../../../api/http/jsonDecoding";
import type { VitaAssembly } from "../../../api/threads/batchesApi";
import type { Extraction } from "../../../api/knowledgeData/knowledgeDataDecoders";
import type { InstanceStatement } from "../../../api/knowledgeData/knowledgeDataDecoders";
import type { InstanceDataSet } from "../../../api/threads/liveMessages";
import { readableTriple } from "../../../shared/formatting/triples";
import { parseNTriple, RDF_TYPE } from "../../../shared/rdf/nTriples";
import { amountReadingOf, dateReadingOf, textFactsOf, type AmountReadingShape, type DateReadingShape, type TextFactsShape } from "./valueReadings";
import { orderedLabels } from "../../ontology/viewer/ontologyModel";

/** Predicates that name an individual: its name, not a row. */
const NAME_PREDICATES: ReadonlySet<string> = new Set([
  "http://www.w3.org/2000/01/rdf-schema#label",
  "http://www.w3.org/2004/02/skos/core#prefLabel",
]);

export interface Individual {
  /** `<iri>`, as N-Triples writes it. */
  readonly iri: string;
  /** The label the data gives it, in the reader's language where it has one; `null` without. */
  readonly name: string | null;
  /** The words of the first statement that types it, which a page may name it by without a label. */
  readonly words: string | null;
  /** The IRIs of the classes it is typed with. */
  readonly classes: string[];
  readonly lines: { triple: string; text: string; statement: InstanceStatement }[];
}

/**
 * The individuals the statements describe, by subject, with their names and classes (F94): the
 * one grouping of instance data, for the result's view and a document's regest. Keep the
 * server's claim numbers: one statement may describe several individuals.
 */
export function groupIndividuals(statements: readonly InstanceStatement[], language: string): Individual[] {
  const groups = new Map<string, Individual & { classes: string[]; lines: Individual["lines"][number][]; labels: TermLabel[]; words: string | null }>();
  // Its name is a label the data gives it; its words, those of the first statement that types it.
  for (const statement of statements) {
    for (const triple of statement.triples) {
      const parsed = parseNTriple(triple);
      if (parsed?.subject.kind !== "iri") continue;
      const iri = `<${parsed.subject.iri}>`;
      let group = groups.get(iri);
      if (group === undefined) {
        group = { iri, name: null, words: null, classes: [], lines: [], labels: [] };
        groups.set(iri, group);
      }
      const { predicate, object } = parsed;
      if (predicate === RDF_TYPE && object.kind === "iri" && !group.classes.includes(object.iri)) group.classes.push(object.iri);
      if (NAME_PREDICATES.has(predicate) && object.kind === "literal") group.labels.push(object);
      if (predicate === RDF_TYPE && group.words === null && statement.evidence.length > 0) group.words = statement.evidence.map((span) => span.quote).join(" … ");
      group.lines.push({ triple, text: readableTriple(triple), statement });
    }
  }
  const firstOffset = (group: Individual) => Math.min(...group.lines.flatMap(({ statement }) => statement.evidence.map((span) => span.start)));
  return [...groups.values()]
    .map(({ iri, classes, lines, labels, words }) => ({ iri, name: orderedLabels(labels, language)[0]?.value ?? null, words, classes, lines }))
    .sort((a, b) => firstOffset(a) - firstOffset(b));
}

/** A value a statement gives a property: an individual the data describes, a model term, or text. */
export type StatementValue =
  | { readonly kind: "individual"; readonly individual: Individual }
  | { readonly kind: "term"; readonly iri: string }
  | { readonly kind: "text"; readonly text: string };

/** What one statement says about one individual: its properties, each with its values. */
export interface StatementDescription {
  readonly individual: Individual;
  readonly rows: readonly { readonly property: string; readonly values: readonly StatementValue[] }[];
}

/**
 * What a statement says, individual by individual, as Daniel's regest viewer shows it (F94):
 * the properties other than its classes and names; an object the data describes as that
 * individual, a term of the data model (`isTerm`, the term owner's answer) as the term, and
 * any other IRI as an individual the data only names. `individuals` is `groupIndividuals`' answer.
 */
export function describeStatement(statement: InstanceStatement, individuals: readonly Individual[], isTerm: (iri: string) => boolean): StatementDescription[] {
  const byIri = new Map(individuals.map((individual) => [individual.iri, individual]));
  const descriptions = new Map<string, Map<string, StatementValue[]>>();
  for (const triple of statement.triples) {
    const parsed = parseNTriple(triple);
    if (parsed?.subject.kind !== "iri") continue;
    const subject = `<${parsed.subject.iri}>`;
    const rows = descriptions.get(subject) ?? new Map<string, StatementValue[]>();
    descriptions.set(subject, rows);
    const { predicate, object } = parsed;
    if (predicate === RDF_TYPE || NAME_PREDICATES.has(predicate)) continue;
    let value: StatementValue;
    if (object.kind !== "iri") {
      value = { kind: "text", text: object.kind === "literal" ? object.text : object.label };
    } else {
      const described = byIri.get(`<${object.iri}>`);
      value = described !== undefined ? { kind: "individual", individual: described }
        : isTerm(object.iri) ? { kind: "term", iri: object.iri }
        : { kind: "individual", individual: { iri: `<${object.iri}>`, name: null, words: null, classes: [], lines: [] } };
    }
    rows.set(predicate, [...(rows.get(predicate) ?? []), value]);
  }
  return [...descriptions].flatMap(([iri, rows]) => {
    const individual = byIri.get(iri);
    return individual === undefined ? [] : [{ individual, rows: [...rows].map(([property, values]) => ({ property, values })) }];
  });
}

/**
 * How a project lays out its instance data (C-B7 AB-5), from the `dmw-abox-view/1` artifact it
 * binds to `abox_view`: the classes and properties of the evidence layer, which anchors a
 * statement in its text rather than saying what the text says.
 */
export interface AboxViewProfile {
  readonly evidenceClasses: ReadonlySet<string>;
  readonly evidenceProperties: ReadonlySet<string>;
  /** How one statement reads (Daniel's statement card); `null` shows what it says as written. */
  readonly statements: StatementCardShape | null;
  /** The groups of the cards of persons, places and the like after the texts, in order; `[]` for none. */
  readonly entities: readonly EntityGroupShape[];
  /** The properties by which an evidence individual stands for one entity (a mention's way to its entity); empty for none. */
  readonly entityLinks: ReadonlySet<string>;
  /** Statements concluded from others: a row of `premises` names what another statement says; `null` for none. */
  readonly conclusions: { readonly premises: string } | null;
  /** What heads each text: its date and archive reference (`valueReadings`); `null` for none. */
  readonly texts: TextFactsShape | null;
  /** How a date reads as one date; `null` shows dates as written. */
  readonly dates: DateReadingShape | null;
  /** How an amount reads as one amount; `null` shows amounts as written. */
  readonly amounts: AmountReadingShape | null;
}

/** One group of entity cards (Daniel's viewer): the individuals of `classes`, or of a class under one, under `title`. */
export interface EntityGroupShape {
  readonly classes: readonly string[];
  readonly title: readonly TermLabel[];
}

/**
 * Daniel's statement card, as data: `content` names the properties by which a statement says what
 * it is about, an event or a state, whose class is its kind; `participation` reads who takes part
 * in that as one line, "actor as role".
 */
export interface StatementCardShape {
  readonly content: ReadonlySet<string>;
  readonly participation: { readonly property: string; readonly actor: string; readonly role: string } | null;
}

/** A project without a profile: nothing is evidence, every row shows (AB-5's neutral fallback). */
export const NO_VIEW_PROFILE: AboxViewProfile = { evidenceClasses: new Set(), evidenceProperties: new Set(), statements: null, entities: [], entityLinks: new Set(), conclusions: null, texts: null, dates: null, amounts: null };

/** A view profile from the definition of the artifact a project binds to `abox_view`; any other artifact lays nothing out. */
export function viewProfileOf(definition: JsonObject | null): AboxViewProfile {
  const content = definition?.["schema"] === "dmw-abox-view/1" ? definition["content"] : null;
  const evidence = isJsonObject(content) ? content["evidence"] : null;
  if (!isJsonObject(content) || !isJsonObject(evidence)) return NO_VIEW_PROFILE;
  const iris = (value: unknown) => new Set(Array.isArray(value) ? value.filter((iri): iri is string => typeof iri === "string") : []);
  const card = content["statements"];
  const participation = isJsonObject(card) ? card["participation"] : null;
  const { property, actor, role } = isJsonObject(participation) ? participation : {};
  const groups = content["entities"];
  const conclusions = content["conclusions"];
  const premises = isJsonObject(conclusions) ? conclusions["premises"] : null;
  return {
    evidenceClasses: iris(evidence["classes"]),
    evidenceProperties: iris(evidence["properties"]),
    statements: isJsonObject(card) ? {
      content: iris(card["content"]),
      participation: typeof property === "string" && typeof actor === "string" && typeof role === "string" ? { property, actor, role } : null,
    } : null,
    entities: Array.isArray(groups) ? groups.filter(isJsonObject).map((group) => ({
      classes: [...iris(group["classes"])],
      title: (Array.isArray(group["title"]) ? group["title"] : []).filter(isJsonObject).flatMap((title) => {
        const { language, text } = title;
        return typeof language === "string" && typeof text === "string" ? [{ language, text }] : [];
      }),
    })) : [],
    entityLinks: iris(content["entity_links"]),
    conclusions: typeof premises === "string" ? { premises } : null,
    texts: textFactsOf(content["texts"]),
    dates: dateReadingOf(content["dates"]),
    amounts: amountReadingOf(content["amounts"]),
  };
}

/** A statement's descriptions as the view lays them out: what it says, then its evidence, folded. */
export interface StatementLayout {
  readonly said: readonly StatementDescription[];
  readonly evidence: readonly StatementDescription[];
}

/**
 * The one rule that sets a statement's evidence apart (Demo 2's accept step, 4 Nov): an individual
 * of an evidence class, or of a class under one by the data model's parents, is evidence with all
 * its rows. Any other keeps its rows, but for those of an evidence property or whose every value is
 * evidence, which go with the evidence under its name. The data stays as written; only where it
 * shows changes.
 */
/** Whether an individual is of an evidence class, or of a class under one by the data model's parents (AB-5). */
export function isEvidenceIndividual(individual: Individual, profile: AboxViewProfile, parentsOf: (iri: string) => readonly string[]): boolean {
  return [...individual.classes, ...classesAbove(individual.classes, parentsOf)].some((iri) => profile.evidenceClasses.has(iri));
}

export function layOutStatement(descriptions: readonly StatementDescription[], profile: AboxViewProfile, parentsOf: (iri: string) => readonly string[]): StatementLayout {
  const isEvidence = (individual: Individual) => isEvidenceIndividual(individual, profile, parentsOf);
  const isEvidenceRow = (row: StatementDescription["rows"][number]) => profile.evidenceProperties.has(row.property)
    || (row.values.length > 0 && row.values.every((value) => value.kind === "individual" && isEvidence(value.individual)));
  const said: StatementDescription[] = [];
  const evidence: StatementDescription[] = [];
  for (const description of descriptions) {
    if (isEvidence(description.individual)) {
      evidence.push(description);
      continue;
    }
    const links = description.rows.filter(isEvidenceRow);
    said.push(links.length === 0 ? description : { ...description, rows: description.rows.filter((row) => !links.includes(row)) });
    if (links.length > 0) evidence.push({ individual: description.individual, rows: links });
  }
  return { said, evidence };
}

/** A statement to show on its own, how it is laid out, and the statements it shows for: those that only name an individual it gives as a value. */
export interface ShownStatement<T> {
  readonly shown: T;
  readonly layout: StatementLayout;
  readonly absorbed: readonly T[];
}

/**
 * Statements shown together, without repeating one (O-1): a statement that only types and names
 * individuals another of them shows as values (a Place "Sonstwo" that a Petition's "benefice in"
 * names) is shown by that value, with its class, and not again on its own, unless it has a note.
 * Only a value shown open counts, not one under the other's evidence: `layOut`, the statement's
 * `layOutStatement`, is the one answer both read (Sol review R1, 4 Nov). The first statement that
 * names it takes it; order is kept.
 */
export function absorbNamedIndividuals<T extends { readonly statement: InstanceStatement }>(items: readonly T[], layOut: (item: T) => StatementLayout): ShownStatement<T>[] {
  const laidOut = items.map((item) => ({ item, layout: layOut(item) }));
  const namedOnly = ({ said, evidence }: StatementLayout) => said.length + evidence.length > 0 && [...said, ...evidence].every((description) => description.rows.length === 0);
  const valuesShown = ({ said }: StatementLayout) => new Set(said.flatMap((description) => description.rows.flatMap((row) => row.values.flatMap((value) => (value.kind === "individual" ? [value.individual.iri] : [])))));
  const absorber = new Map<T, T>();
  for (const { item, layout } of laidOut) {
    // One with a note keeps its own line, where the note shows.
    if (!namedOnly(layout) || item.statement.note !== "") continue;
    const named = [...layout.said, ...layout.evidence].map((description) => description.individual.iri);
    const by = laidOut.find((other) => other.item !== item && !namedOnly(other.layout) && named.every((iri) => valuesShown(other.layout).has(iri)));
    if (by !== undefined) absorber.set(item, by.item);
  }
  return laidOut
    .filter(({ item }) => !absorber.has(item))
    .map(({ item, layout }) => ({ shown: item, layout, absorbed: items.filter((other) => absorber.get(other) === item) }));
}

/** A description with what the same statement says about its values drawn under them. */
export interface NestedDescription {
  readonly individual: Individual;
  readonly rows: readonly { readonly property: string; readonly values: readonly (StatementValue | { readonly kind: "nested"; readonly description: NestedDescription })[] }[];
}

/**
 * What a statement says, read as one whole (usability round 1, 4 Nov): each description that a
 * value of another names (a claim's event, the event's participations) sits under that value,
 * once; the rest are its roots, in order. Nothing is dropped: a description no root reaches,
 * as in a cycle, is a root of its own.
 */
export function nestSaid(said: readonly StatementDescription[]): NestedDescription[] {
  const byIri = new Map(said.map((description) => [description.individual.iri, description]));
  const named = new Set(said.flatMap((description) => description.rows.flatMap((row) => row.values.flatMap((value) => (value.kind === "individual" && value.individual.iri !== description.individual.iri ? [value.individual.iri] : [])))));
  const placed = new Set<string>();
  const nest = (description: StatementDescription): NestedDescription => {
    placed.add(description.individual.iri);
    return {
      individual: description.individual,
      rows: description.rows.map((row) => ({
        property: row.property,
        values: row.values.map((value) => {
          const inner = value.kind === "individual" ? byIri.get(value.individual.iri) : undefined;
          return inner === undefined || placed.has(inner.individual.iri) ? value : { kind: "nested" as const, description: nest(inner) };
        }),
      })),
    };
  };
  const nested = said.filter((description) => !named.has(description.individual.iri)).map(nest);
  for (const description of said) if (!placed.has(description.individual.iri)) nested.push(nest(description));
  return nested;
}

/** A part's lines as they are read: those that say something first, in text order, then those that are only evidence (AB-5). */
export function saidFirst<T>(items: readonly ShownStatement<T>[]): ShownStatement<T>[] {
  return [...items.filter((item) => item.layout.said.length > 0), ...items.filter((item) => item.layout.said.length === 0)];
}

/** Its classes without those above another of them (`Petition`, not also `Event`), by the data model's parents. */
export function mostSpecificClasses(classes: readonly string[], parentsOf: (iri: string) => readonly string[]): string[] {
  const general = classesAbove(classes, parentsOf);
  return classes.filter((iri) => !general.has(iri));
}

/** Every class above one of `classes` by the data model's parents. */
export function classesAbove(classes: readonly string[], parentsOf: (iri: string) => readonly string[]): Set<string> {
  const above = new Set<string>();
  const todo = classes.flatMap(parentsOf);
  for (let next = todo.pop(); next !== undefined; next = todo.pop()) {
    if (above.has(next)) continue;
    above.add(next);
    todo.push(...parentsOf(next));
  }
  return above;
}

export function wholeGraph(statements: readonly InstanceStatement[]): string {
  return [...new Set(statements.flatMap((statement) => statement.triples))].join("\n");
}

/** Statements not recorded as one result: a proposal under review, or a vita assembled from its parts' results. */
export interface UnrecordedInstanceData {
  /** What its statements are keyed by and asked about as: the Thread entry that holds them, where there is one. */
  readonly source: string;
  /** Where selecting their words asks the agent about them (F96); `undefined`: not asked about here. */
  readonly projectId: string | undefined;
  /** The project they belong to, whose view profile and terms show them (AB-5); `null` when not known. */
  readonly project: string | null;
  /** The document version (`<document>@<version>`) whose text they are drawn on. */
  readonly document: string;
  /** The model version they were written against (`<collection>@<commit>`), where their terms open. */
  readonly model: string;
  readonly accepted: boolean;
  readonly instanceStatements: readonly InstanceStatement[];
}

/**
 * A vita assembled from its parts' results as the instance-data view draws it: on the vita's
 * text, its terms opening at the first part's model version, accepted once every part is.
 */
export function assembledVita(vita: string, parts: readonly Pick<Extraction, "model" | "state" | "projectId">[], statements: readonly InstanceStatement[]): UnrecordedInstanceData {
  return {
    source: vita,
    projectId: undefined,
    project: parts[0]?.projectId ?? null,
    document: vita,
    model: parts[0]?.model ?? "",
    accepted: parts.length > 0 && parts.every((part) => part.state === "accepted"),
    instanceStatements: statements,
  };
}

/**
 * A step's proposed set as statements the instance-data view draws (F154): numbered in its
 * order from 1, as the text an agent reads numbers them; without IRIs, which recording gives.
 */
export function proposedStatements(set: InstanceDataSet): InstanceStatement[] {
  return set.statements.map((statement, index) => ({
    number: index + 1,
    iri: "",
    note: statement.note,
    triples: statement.triples,
    evidence: statement.evidence.map((span) => ({ ...span, segmentIri: "" })),
  }));
}

/** Match assemble_vita: plan-order statements, sorted triple keys, document evidence only. */
export function assembleResults(manifest: VitaAssembly, results: readonly Pick<Extraction, "source" | "instanceStatements">[]) {
  const groups = new Map<string, { statements: InstanceStatement[]; parts: Set<string> }>();
  for (const output of manifest.partOutputs) {
    const result = results.find((result) => result.source === output);
    if (result === undefined) throw new Error(`Missing part output: ${output}`);
    for (const statement of result.instanceStatements) {
      if (!statement.evidence.some((span) => span.segment.split("#")[0] === manifest.vita)) continue;
      const key = JSON.stringify([...statement.triples].sort());
      const group = groups.get(key) ?? { statements: [], parts: new Set<string>() };
      group.statements.push(statement);
      group.parts.add(output);
      groups.set(key, group);
    }
  }
  const membership = [...groups.values()].map((group) => [...group.parts].sort());
  if (JSON.stringify(membership) !== JSON.stringify(manifest.membership)) throw new Error("Assembly membership differs from its part results.");
  const statements = [...groups.values()].map((group, index): InstanceStatement => {
    const first = group.statements[0];
    if (first === undefined) throw new Error("Empty assembly statement.");
    const evidence = new Map(group.statements.flatMap((statement) => statement.evidence).map((span) => [JSON.stringify([span.segment, span.start, span.end]), span]));
    return { ...first, number: index + 1, triples: [...first.triples].sort(), evidence: [...evidence.values()], note: [...new Set(group.statements.map((statement) => statement.note).filter(Boolean))].join(" / ") };
  });
  return { statements, membership, complete: manifest.complete, missingUnits: manifest.missingUnits };
}
